import { motion } from 'framer-motion'
import { MerchantLocation } from '@/types'
import { MapPin, Phone, Star, Clock, Navigation, Heart, Percent } from 'lucide-react'
import { Button } from '@/components/ui/Button'

interface MerchantCardProps {
  merchant: MerchantLocation
  delay?: number
  onToggleFavorite?: (merchant: MerchantLocation) => void
  onAddReview?: (merchant: MerchantLocation) => void
}

export function MerchantCard({ merchant, delay = 0, onToggleFavorite, onAddReview }: MerchantCardProps) {
  const getOpeningHours = () => {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
    const hours = merchant.opening_hours?.[today]
    if (!hours) return 'Horário não disponível'
    
    if (hours.open === 'closed' || hours.close === 'closed') {
      return 'Fechado hoje'
    }
    
    return `${hours.open} - ${hours.close}`
  }

  const getStatusColor = () => {
    const now = new Date()
    const today = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
    const hours = merchant.opening_hours?.[today]
    
    if (!hours || hours.open === 'closed' || hours.close === 'closed') {
      return 'text-error'
    }
    
    const currentTime = now.toTimeString().slice(0, 5)
    if (currentTime >= hours.open && currentTime <= hours.close) {
      return 'text-success'
    }
    
    return 'text-warning'
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 card-hover"
    >
      <div className="flex items-start space-x-3">
        {/* Merchant Image */}
        <div className="w-16 h-16 bg-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
          {merchant.photos && merchant.photos.length > 0 ? (
            <img 
              src={merchant.photos[0]} 
              alt={merchant.location_name}
              className="w-full h-full object-cover rounded-xl"
            />
          ) : (
            <MapPin className="h-8 w-8 text-gray-400" />
          )}
        </div>

        {/* Merchant Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="font-semibold text-text-primary truncate">
                {merchant.location_name}
              </h3>
              <p className="text-sm text-text-secondary truncate">
                {merchant.address}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {merchant.has_active_promotion && (
                <span className="inline-flex items-center text-xs text-white bg-green-500 px-2 py-1 rounded-full">
                  <Percent className="h-3 w-3 mr-1" /> Promo
                </span>
              )}
              {merchant.distance && (
                <span className="text-xs text-text-secondary bg-gray-100 px-2 py-1 rounded-full">
                  {merchant.distance.toFixed(1)} km
                </span>
              )}
              <button
                aria-label="favorite"
                className={`p-1 rounded-full ${ (merchant as any).is_favorite ? 'text-red-500' : 'text-gray-400'}`}
                onClick={() => onToggleFavorite && onToggleFavorite(merchant)}
              >
                <Heart className={`h-5 w-5 ${ (merchant as any).is_favorite ? 'fill-current' : ''}`} />
              </button>
            </div>
          </div>

          {/* Rating and Category */}
          <div className="flex items-center space-x-2 mb-2">
            {merchant.average_rating && (
              <div className="flex items-center space-x-1">
                <Star className="h-4 w-4 text-warning fill-current" />
                <span className="text-sm font-medium text-text-primary">
                  {merchant.average_rating.toFixed(1)}
                </span>
              </div>
            )}
            {merchant.reviews_count !== undefined && (
              <span className="text-xs text-text-secondary">({merchant.reviews_count})</span>
            )}
            {merchant.category && (
              <span className="text-xs text-text-secondary bg-primary/10 text-primary px-2 py-1 rounded-full">
                {merchant.category.name}
              </span>
            )}
          </div>

          {/* Opening Hours */}
          <div className="flex items-center space-x-1 mb-3">
            <Clock className="h-4 w-4 text-text-secondary" />
            <span className={`text-sm ${getStatusColor()}`}>
              {getOpeningHours()}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            <Button
              variant="primary"
              size="sm"
              className="flex-1"
            >
              Pagar QR
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
            >
              <Navigation className="h-4 w-4 mr-1" />
              Navegar
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="p-2"
              onClick={() => onAddReview && onAddReview(merchant)}
            >
              <Star className="h-4 w-4" />
            </Button>
            {merchant.phone_number && (
              <Button
                variant="ghost"
                size="sm"
                className="p-2"
              >
                <Phone className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      {merchant.description && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-sm text-text-secondary line-clamp-2">
            {merchant.description}
          </p>
        </div>
      )}

      {/* Amenities */}
      {merchant.amenities && merchant.amenities.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center space-x-2 flex-wrap">
            {merchant.amenities.slice(0, 3).map((amenity, index) => (
              <span 
                key={index}
                className="text-xs text-text-secondary bg-gray-100 px-2 py-1 rounded-full"
              >
                {amenity}
              </span>
            ))}
            {merchant.amenities.length > 3 && (
              <span className="text-xs text-text-secondary">
                +{merchant.amenities.length - 3} mais
              </span>
            )}
          </div>
        </div>
      )}
    </motion.div>
  )
}
